import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, formatDate } from '../../lib/api';
import { esMovil } from '../../lib/utils';
import { Button } from '../ui/button';
import { FlaskConical, Upload, Eye, Download, Trash2, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const PREVISUALIZABLES = ['application/pdf', 'image/png', 'image/jpeg', 'image/webp', 'image/gif'];

function tamano(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Pruebas preoperatorias del paciente (analítica, ECG…): las manda el
 * paciente, administración las sube y el anestesista las abre en pantalla.
 * Se usa en la ficha y, en versión compacta, en el Parte del día de quirófano.
 *
 * Props: patientId · onPreview({url, filename, title}) · compact · canDelete
 */
export default function PruebasPreopCard({ patientId, onPreview, compact = false, canDelete = true }) {
  const [docs, setDocs] = useState(null);
  const [subiendo, setSubiendo] = useState(0);
  const inputRef = useRef(null);

  const cargar = useCallback(() => {
    if (!patientId) return;
    api.get(`/api/patients/${patientId}/documentos`)
      .then((r) => setDocs(Array.isArray(r.data) ? r.data : []))
      .catch(() => setDocs([]));
  }, [patientId]);

  useEffect(() => { cargar(); }, [cargar]);

  const subir = async (files) => {
    const lista = Array.from(files || []).filter(Boolean);
    if (!lista.length) return;
    setSubiendo((n) => n + lista.length);
    for (const f of lista) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        await api.post(`/api/patients/${patientId}/documentos`, fd, { params: { categoria: 'preoperatorio' } });
        toast.success(`Subido: ${f.name}`);
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || 'no se pudo subir'}`);
      } finally {
        setSubiendo((n) => n - 1);
      }
    }
    cargar();
  };

  const borrar = async (d) => {
    if (!window.confirm(`¿Eliminar «${d.name}»?`)) return;
    try {
      await api.delete(`/api/patients/${patientId}/documentos/${d.id}`);
      toast.success('Eliminado');
      cargar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    }
  };

  const urlDe = (d) => `${BACKEND_URL}/api/patients/${patientId}/documentos/${d.id}/download`;

  return (
    <div data-testid="pruebas-preop">
      <div className="flex items-center justify-between gap-2 mb-2">
        {!compact && (
          <span className="text-xs text-muted-foreground">
            Analítica, ECG y demás pruebas que mande el paciente — el anestesista las ve aquí.
          </span>
        )}
        <Button
          size="sm"
          variant="outline"
          className="h-8"
          onClick={() => inputRef.current?.click()}
          disabled={subiendo > 0}
          data-testid="pruebas-preop-subir"
        >
          {subiendo > 0 ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1.5" />}
          Subir prueba
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*,.doc,.docx"
          className="hidden"
          onChange={(e) => { subir(e.target.files); e.target.value = ''; }}
        />
      </div>
      {docs === null ? (
        <div className="text-sm text-muted-foreground py-2">Cargando…</div>
      ) : docs.length === 0 ? (
        <div className="text-sm text-muted-foreground py-2 flex items-center gap-2">
          <FlaskConical className="w-4 h-4" /> Sin pruebas subidas.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {docs.map((d) => (
            <div key={d.id} className="py-1.5 flex items-center gap-2" data-testid="pruebas-preop-doc">
              <FlaskConical className="w-4 h-4 text-[#2E6E5E] shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#13231E] truncate">{d.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {tamano(d.size)} · {d.created_by_name} · {formatDate((d.created_at || '').slice(0, 10))}
                </div>
              </div>
              {PREVISUALIZABLES.includes(d.mime) && onPreview && (
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Ver"
                  onClick={() => onPreview({ url: urlDe(d), filename: d.name, title: d.name })}>
                  <Eye className="w-3.5 h-3.5" />
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-8 w-8 p-0" title="Descargar"
                onClick={() => {
                  // En móvil, pestaña nueva: el enlace con `download` navegaba
                  // dentro de la app y bloqueaba el atrás en iOS (2026-07-25).
                  if (esMovil()) {
                    window.open(urlDe(d), '_blank', 'noopener');
                    return;
                  }
                  const a = document.createElement('a');
                  a.href = urlDe(d);
                  a.download = d.name;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                }}>
                <Download className="w-3.5 h-3.5" />
              </Button>
              {canDelete && (
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" title="Eliminar" onClick={() => borrar(d)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
